/**
 * LibraryView — redesigned Library tab UI
 *
 * Features:
 * - Sort pills: Recent | Name | Type
 * - Filter dropdown: All items | Wallpapers | Icon packs | Ringtones
 * - 2-column card grid with tall aspect-ratio cards
 * - Verified badge on each card
 * - Demo data (6 items) blended with real purchased items when available
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Filter,
  CheckCircle,
  Music,
  RotateCcw,
  Check,
  BookOpen,
  ImageOff,
  Layers,
  SearchX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';
import { useRingtonePlayer } from '@/contexts/RingtonePlayerContext';
import { MiniPlayerBar } from '@/components/MiniPlayerBar';
import { useLongPress } from '@/hooks/use-long-press';

// ─── Types ───────────────────────────────────────────────────────────────────

type SortKey = 'recent' | 'name' | 'type';
type FilterKey = 'all' | 'wallpaper' | 'live-wallpaper' | 'icon' | 'ringtone';
type ItemType = 'wallpaper' | 'live-wallpaper' | 'icon' | 'ringtone';

type DemoItem = {
  id: string;
  title: string;
  type: ItemType;
  // wallpapers: gradient or imageUrl; icon packs: tile definitions
  gradient?: string;
  imageUrl?: string;
  icons?: { bg: string; Icon: React.ElementType; color: string }[];
  onClick?: () => void;
  /** Underlying wallpaper record (only present for real, non-demo items) */
  wallpaper?: WallpapersResponse;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const EMPTY_SELECTION: Set<string> = new Set();

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'All items',
  wallpaper: 'Wallpapers',
  'live-wallpaper': 'Live wallpapers',
  icon: 'Icon packs',
  ringtone: 'Ringtones',
};

const TYPE_LABELS: Record<ItemType, string> = {
  wallpaper: 'WALLPAPER',
  'live-wallpaper': 'LIVE',
  icon: 'ICON PACK',
  ringtone: 'RINGTONE',
};

function IconPackTiles({ icons }: { icons: NonNullable<DemoItem['icons']> }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: '#111118',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 8,
        padding: 20,
        boxSizing: 'border-box',
      }}
    >
      {icons.map((item, i) => {
        const { bg, Icon, color } = item;
        return (
          <div
            key={i}
            style={{
              borderRadius: 14,
              background: bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={26} color={color} strokeWidth={1.8} />
          </div>
        );
      })}
    </div>
  );
}

// ─── Multi-select — long-press to enter, tap to toggle ───────────────────────
//
// SelectionIndicator replaces whatever circular badge a card/row already shows
// (verified check, favorite check) so there is always exactly one circular
// indicator per item. Follows the app's restrained "active surface" pattern —
// neutral glass/white when selected, translucent outline when not — no color
// tint, border glow, or shimmer.

function SelectionIndicator({ selected }: { selected: boolean }) {
  return (
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        flexShrink: 0,
        background: selected ? 'rgba(255,255,255,0.92)' : 'rgba(10,10,18,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${selected ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.32)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.18s ease, border-color 0.18s ease',
      }}
    >
      {selected && <Check size={12} color="#0a0a12" strokeWidth={3} />}
    </div>
  );
}

// Shared long-press-to-select + tap-to-toggle wiring, used by both the visual
// cards and the ringtone rows so the gesture logic lives in exactly one place.
function useSelectableTap(
  itemId: string,
  selectionMode: boolean,
  onLongPressItem: ((id: string) => void) | undefined,
  onToggleSelect: ((id: string) => void) | undefined,
  onClick?: () => void,
) {
  const lp = useLongPress({
    delay: 550,
    onLongPress: () => onLongPressItem?.(itemId),
  });

  const handleTap = () => {
    if (lp.triggered()) {
      lp.clearTriggered();
      return;
    }
    if (selectionMode) {
      onToggleSelect?.(itemId);
      return;
    }
    onClick?.();
  };

  const pressHandlers = {
    onMouseDown: lp.onMouseDown,
    onMouseUp: lp.onMouseUp,
    onMouseMove: lp.onMouseMove,
    onMouseLeave: lp.onMouseLeave,
    onTouchStart: lp.onTouchStart,
    onTouchEnd: lp.onTouchEnd,
    onTouchCancel: lp.onTouchCancel,
    onTouchMove: lp.onTouchMove,
  };

  return { handleTap, pressHandlers };
}

// ─── Ringtone row (matches Explore's RingtoneRow exactly) ────────────────────

function LibraryRingtoneRow({
  item,
  index,
  isFavorited,
  onToggleFavorite,
  selectionMode = false,
  isSelected = false,
  onLongPressItem,
  onToggleSelect,
}: {
  item: DemoItem;
  index: number;
  isFavorited: boolean;
  onToggleFavorite?: (w: WallpapersResponse) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onLongPressItem?: (id: string) => void;
  onToggleSelect?: (id: string) => void;
}) {
  const [duration, setDuration] = useState(0);
  const probeRef = useRef<HTMLAudioElement>(null);

  // Use global ringtone player so state stays in sync with MiniPlayer / FullScreenPlayer
  const { currentRingtone, isPlaying: globalPlaying, currentTime, duration: globalDuration, togglePlayPause } = useRingtonePlayer();

  const isThisTrack = currentRingtone?.id === item.id;
  // Show active state whenever this track is the current track and audio is playing.
  const isPlaying = isThisTrack && globalPlaying;
  const progress = isThisTrack && globalDuration > 0
    ? (currentTime / globalDuration) * 100
    : 0;

  const handleProbeLoaded = () => {
    const a = probeRef.current;
    if (a && isFinite(a.duration)) setDuration(a.duration);
  };

  const durationSecs = duration > 0 ? Math.round(duration) : null;
  const category = TYPE_LABELS[item.type] ?? 'Ringtone';
  const subtitle = durationSecs != null ? `${category} • ${durationSecs}s` : category;

  const handlePlayToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.onClick && item.imageUrl) {
      const fakeW = { id: item.id, title: item.title, imageUrl: item.imageUrl } as WallpapersResponse;
      togglePlayPause(fakeW, [fakeW]);
    }
  };

  const { handleTap, pressHandlers } = useSelectableTap(item.id, selectionMode, onLongPressItem, onToggleSelect, item.onClick);

  // While bulk-select mode is active, de-emphasize items that aren't selected
  // via opacity only (no filters/scale/border changes) so selected items read
  // more prominently. Off entirely when selection mode is inactive.
  const dimmed = selectionMode && !isSelected;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: dimmed ? 0.5 : 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: [0.22, 1, 0.36, 1], opacity: { duration: 0.15 } }}
      whileHover={{ scale: 1.02, y: -2, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
      whileTap={{ scale: 0.98, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
      onContextMenu={(e) => e.preventDefault()}
      {...pressHandlers}
      style={{
        width: '100%',
        willChange: 'transform',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      } as React.CSSProperties}
    >
      {/* Silent metadata probe */}
      <audio
        ref={probeRef}
        src={item.imageUrl ?? ''}
        preload="metadata"
        onLoadedMetadata={handleProbeLoaded}
        style={{ display: 'none' }}
      />

      {/* The row stays the same player in selection mode — only the right-side
          controls swap for a single selection checkmark. */}
      <MiniPlayerBar
        title={item.title}
        subtitle={subtitle}
        isPlaying={isPlaying}
        progress={progress}
        onPlayPause={handlePlayToggle}
        showNextButton={false}
        showDismissButton={false}
        onBarClick={handleTap}
        showFavoriteButton={!!item.wallpaper && !!onToggleFavorite}
        favoriteVariant="check"
        isFavorited={isFavorited}
        onToggleFavorite={(e) => {
          e.stopPropagation();
          if (item.wallpaper && onToggleFavorite) onToggleFavorite(item.wallpaper);
        }}
        controlsOverride={selectionMode ? <SelectionIndicator selected={isSelected} /> : undefined}
      />
    </motion.div>
  );
}

// ─── Library card — Compact variant (3-column, ~140px image, title only, no category) ───

const LibraryCard = React.memo(function LibraryCard({
  item,
  index,
  selectionMode = false,
  isSelected = false,
  onLongPressItem,
  onToggleSelect,
}: {
  item: DemoItem;
  index: number;
  selectionMode?: boolean;
  isSelected?: boolean;
  onLongPressItem?: (id: string) => void;
  onToggleSelect?: (id: string) => void;
}) {
  const { handleTap, pressHandlers } = useSelectableTap(item.id, selectionMode, onLongPressItem, onToggleSelect, item.onClick);

  // While bulk-select mode is active, de-emphasize items that aren't selected
  // via opacity only (no filters/scale/border changes) so selected items read
  // more prominently. Off entirely when selection mode is inactive.
  const dimmed = selectionMode && !isSelected;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: dimmed ? 0.5 : 1, y: 0 }}
        transition={{ duration: 0.32, delay: index * 0.06, ease: [0.22, 1, 0.36, 1], opacity: { duration: 0.15 } }}
        whileHover={{ scale: 1.03, y: -3, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
        whileTap={{ scale: 0.97, transition: { type: 'spring', stiffness: 320, damping: 22 } }}
        onClick={handleTap}
        onContextMenu={(e) => e.preventDefault()}
        {...pressHandlers}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
          willChange: 'transform',
        } as React.CSSProperties}
      >
      {/* Image / artwork area — compact fixed height */}
      <div
        style={{
          position: 'relative',
          height: 140,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#12111a',
          flexShrink: 0,
        }}
      >
        {item.type === 'icon' && item.icons ? (
          <IconPackTiles icons={item.icons} />
        ) : item.type === 'ringtone' ? (
          <>
            {/* Dark warm background */}
            <div style={{
              position: 'absolute', inset: 0,
              background: '#1A1025',
            }} />
            {/* Ambient orange glow */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(251,146,60,0.18) 0%, transparent 70%)',
              animation: 'ringtoneGlow 3s ease-in-out infinite alternate',
            }} />
            {/* Waveform bars */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 2,
            }}>
              {[0.4, 0.7, 1.0, 0.6, 0.9, 0.5, 0.8, 0.4, 0.7, 1.0, 0.6, 0.85, 0.45].map((h, idx) => (
                <div
                  key={idx}
                  style={{
                    width: 2,
                    borderRadius: 2,
                    background: `rgba(251,146,60,${0.25 + h * 0.5})`,
                    height: `${h * 36}px`,
                    animation: `libWaveBar${(idx % 4) + 1} ${0.8 + (idx % 3) * 0.2}s ease-in-out infinite alternate`,
                    animationDelay: `${idx * 0.07}s`,
                  }}
                />
              ))}
            </div>
            {/* Music icon */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: 44, height: 44,
                borderRadius: '50%',
                background: 'rgba(0,229,204,0.12)',
                border: '1px solid rgba(0,229,204,0.28)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'libRingtonePulse 2.4s ease-in-out infinite',
                position: 'relative',
              }}>
                <Music size={20} color="#00E5CC" strokeWidth={1.5} />
              </div>
            </div>
            <style>{`
              @keyframes libRingtonePulse {
                0% { opacity: 0.7; transform: scale(1); }
                100% { opacity: 1; transform: scale(1.06); }
              }
              @keyframes ringtoneGlow {
                0% { opacity: 0.6; }
                100% { opacity: 1; }
              }
              @keyframes libWaveBar1 {
                0% { transform: scaleY(0.5); } 100% { transform: scaleY(1); }
              }
              @keyframes libWaveBar2 {
                0% { transform: scaleY(0.7); } 100% { transform: scaleY(0.4); }
              }
              @keyframes libWaveBar3 {
                0% { transform: scaleY(1); } 100% { transform: scaleY(0.6); }
              }
              @keyframes libWaveBar4 {
                0% { transform: scaleY(0.4); } 100% { transform: scaleY(0.9); }
              }
            `}</style>
          </>
        ) : item.type === 'live-wallpaper' && item.imageUrl ? (
          <video
            src={item.imageUrl}
            autoPlay
            loop
            muted
            playsInline
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              WebkitTouchCallout: 'none',
            } as React.CSSProperties}
          />
        ) : item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.title}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: item.gradient ?? 'linear-gradient(160deg, #1a0030 0%, #3d0073 50%, #6d28d9 100%)',
            }}
          />
        )}

        {/* Verified badge (default) / selection indicator (selection mode) —
             same top-right slot, exactly one circular indicator at a time. */}
        <div style={{ position: 'absolute', top: 7, right: 7 }}>
          {selectionMode ? (
            <SelectionIndicator selected={isSelected} />
          ) : (
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'rgba(10,10,18,0.7)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(52,211,153,0.5)',
              }}
            >
              <CheckCircle size={11} color="#34d399" strokeWidth={2.2} />
            </div>
          )}
        </div>
      </div>

      {/* Title only — no category label in compact view */}
      <p style={{
        margin: 0,
        fontSize: 11,
        fontWeight: 600,
        color: 'rgba(240,240,245,0.82)',
        fontFamily: "'Syne', sans-serif",
        letterSpacing: '-0.01em',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingLeft: 1,
        width: '100%',
        minWidth: 0,
      }}>
        {item.title}
      </p>
    </motion.div>
    </>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

interface LibraryViewProps {
  /** Real purchased wallpapers from the database — may be empty/undefined */
  libraryItems?: WallpapersResponse[];
  /** Whether the user is authenticated */
  isLoggedIn?: boolean;
  /** Called when a real wallpaper card is clicked */
  onSelectWallpaper?: (w: WallpapersResponse, fromLibrary: boolean) => void;
  /** Set of wallpaper IDs the user has favorited */
  favoritedIds?: Set<string>;
  /** Toggle favorite on a real wallpaper record */
  onToggleFavorite?: (w: WallpapersResponse) => void;
  /** Navigate to the Explore tab */
  onBrowse?: () => void;

  /** Whether multi-select mode is active (lifted to the parent so the header
   *  trash action can live outside this component). */
  selectionMode?: boolean;
  /** IDs of currently selected items. */
  selectedIds?: Set<string>;
  /** Called when a long-press triggers on an item — enters selection mode
   *  and selects that item. */
  onLongPressItem?: (id: string) => void;
  /** Called on tap while in selection mode — toggles that item's selection. */
  onToggleSelectItem?: (id: string) => void;
}

// ─── Per-type empty state config ─────────────────────────────────────────────

const FILTER_EMPTY_STATES: Record<FilterKey, {
  Icon: React.ElementType;
  heading: string;
  description: string;
  cta: string;
}> = {
  all: {
    Icon: BookOpen,
    heading: 'Nothing in your library yet',
    description: 'Items you purchase will appear here. Browse the Explore page to find something you love.',
    cta: 'Explore now',
  },
  wallpaper: {
    Icon: ImageOff,
    heading: 'No wallpapers saved',
    description: 'Purchase a wallpaper from the Explore page to add it to your library.',
    cta: 'Explore wallpapers',
  },
  'live-wallpaper': {
    Icon: Layers,
    heading: 'No live wallpapers saved',
    description: 'Discover animated live wallpapers on the Explore page.',
    cta: 'Explore live wallpapers',
  },
  icon: {
    Icon: Layers,
    heading: 'No icon packs saved',
    description: 'Browse icon packs on the Explore page and add them to your library.',
    cta: 'Explore icon packs',
  },
  ringtone: {
    Icon: Music,
    heading: 'No ringtones saved',
    description: 'Pick up a ringtone from the Explore page and it will appear here.',
    cta: 'Explore ringtones',
  },
};

function LibraryEmptyState({
  filterKey,
  isFilterActive,
  onBrowse,
}: {
  filterKey: FilterKey;
  isFilterActive: boolean;
  onBrowse?: () => void;
}) {
  if (isFilterActive) {
    // Filter-specific empty (has items but none match filter)
    const config = FILTER_EMPTY_STATES[filterKey] ?? FILTER_EMPTY_STATES.all;
    return (
      <motion.div
        key="filter-empty"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px 32px',
          gap: 14,
          textAlign: 'center',
        }}
      >
        <div style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'rgba(246,16,55,0.07)',
          border: '1.5px solid rgba(246,246,246,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 2,
        }}>
          <SearchX size={26} color="rgba(240,240,245,0.35)" strokeWidth={1.5} />
        </div>
        <p style={{
          color: 'rgba(240,240,245,0.78)',
          fontSize: 15,
          fontWeight: 700,
          fontFamily: "'Syne', sans-serif",
          margin: 0,
          letterSpacing: '-0.01em',
        }}>
          No {FILTER_EMPTY_STATES[filterKey]?.heading.replace('No ', '').replace(' saved', '') ?? 'items'} in your library
        </p>
        <p style={{
          color: 'rgba(240,240,245,0.35)',
          fontSize: 13,
          fontFamily: "'Syne', sans-serif",
          margin: 0,
          maxWidth: 240,
          lineHeight: 1.55,
        }}>
          {config.description}
        </p>
        {onBrowse && (
          <Button
            variant="outline"
            size="sm"
            onClick={onBrowse}
            style={{
              marginTop: 4,
              borderColor: 'rgba(255,255,255,0.15)',
              color: 'rgba(240,240,245,0.7)',
              fontFamily: "'Syne', sans-serif",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {config.cta}
          </Button>
        )}
      </motion.div>
    );
  }

  // Global empty — no items at all
  const config = FILTER_EMPTY_STATES.all;
  const { Icon } = config;
  return (
    <motion.div
      key="global-empty"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.34, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 32px 100px',
        gap: 16,
        textAlign: 'center',
      }}
    >
      {/* Icon circle */}
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          inset: -16,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(109,40,217,0.1) 100%)',
          border: '1px solid rgba(139,92,246,0.35)',
          boxShadow: '0 0 24px rgba(139,92,246,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={30} color="hsl(262, 83%, 66%)" strokeWidth={1.5} />
        </div>
      </div>

      {/* Text */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
        <p style={{
          color: '#f0f0f5',
          fontSize: 18,
          fontWeight: 800,
          margin: 0,
          fontFamily: "'Syne', sans-serif",
          letterSpacing: '-0.02em',
        }}>
          {config.heading}
        </p>
        <p style={{
          color: 'rgba(240,240,245,0.4)',
          fontSize: 13,
          margin: 0,
          maxWidth: 260,
          lineHeight: 1.55,
          fontFamily: "'Syne', sans-serif",
        }}>
          {config.description}
        </p>
      </div>

      {/* CTA */}
      {onBrowse && (
        <Button
          onClick={onBrowse}
          style={{
            marginTop: 4,
            background: '#a78bfa',
            color: '#fff',
            fontFamily: "'Syne', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            border: 'none',
            boxShadow: '0 4px 20px rgba(167,139,250,0.35)',
            borderRadius: 9999,
            padding: '0 28px',
            height: 42,
          }}
        >
          {config.cta}
        </Button>
      )}
    </motion.div>
  );
}

export function LibraryView({
  libraryItems,
  isLoggedIn,
  onSelectWallpaper,
  favoritedIds: favoritedIdsProp,
  onToggleFavorite,
  onBrowse,
  selectionMode = false,
  selectedIds,
  onLongPressItem,
  onToggleSelectItem,
}: LibraryViewProps) {
  const favoritedIds = favoritedIdsProp ?? new Set<string>();
  const selection = selectedIds ?? EMPTY_SELECTION;
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    try {
      const stored = sessionStorage.getItem('seekscape-library-sort');
      if (stored && ['recent', 'name', 'type'].includes(stored)) return stored as SortKey;
    } catch {}
    return 'recent';
  });
  const [filterKey, setFilterKey] = useState<FilterKey>(() => {
    try {
      const stored = sessionStorage.getItem('seekscape-library-filter');
      if (stored && ['all', 'wallpaper', 'live-wallpaper', 'icon', 'ringtone'].includes(stored)) return stored as FilterKey;
    } catch {}
    return 'all';
  });
  const [filterOpen, setFilterOpen] = useState(false);

  const handleSetSortKey = (key: SortKey) => {
    setSortKey(key);
    try { sessionStorage.setItem('seekscape-library-sort', key); } catch {}
  };
  const handleSetFilterKey = (key: FilterKey) => {
    setFilterKey(key);
    try { sessionStorage.setItem('seekscape-library-filter', key); } catch {}
  };

  const { play, openFullScreen } = useRingtonePlayer();

  // Build display items from real library data only — no demo fallback
  const baseItems = useMemo<DemoItem[]>(() => {
    if (!isLoggedIn || !libraryItems || libraryItems.length === 0) return [];
    return libraryItems.map(w => {
      const mt = (w as WallpapersResponse & { mediaType?: string }).mediaType;
      let resolvedType: ItemType;
      if (mt === 'icon') resolvedType = 'icon';
      else if (mt === 'ringtone') resolvedType = 'ringtone';
      else if (mt === 'live-wallpaper') resolvedType = 'live-wallpaper';
      else {
        // Legacy fallback: detect live wallpaper from URL extension
        const url = (w.imageUrl || '').toLowerCase().split('?')[0];
        resolvedType = /\.(mp4|webm|mov|m4v)$/.test(url) ? 'live-wallpaper' : 'wallpaper';
      }

      // Ringtones open the full-screen ringtone player.
      // Pass the full libraryItems list (not ringtone-filtered) so swipe navigation
      // can cross-type from ringtones into wallpapers/live on the Library page.
      const onClick = resolvedType === 'ringtone'
        ? () => { play(w, libraryItems); openFullScreen(); }
        : () => onSelectWallpaper?.(w, true);

      return {
        id: w.id,
        title: w.title ?? 'Untitled',
        type: resolvedType,
        imageUrl: w.imageUrl,
        onClick,
        wallpaper: w,
      };
    });
  }, [libraryItems, isLoggedIn, onSelectWallpaper, play, openFullScreen]);

  // Determine which item types are actually present — only show those in the filter dropdown
  const availableTypes = useMemo<Set<ItemType>>(() => {
    const seen = new Set<ItemType>();
    baseItems.forEach(item => seen.add(item.type));
    return seen;
  }, [baseItems]);

  // If the active filter is for a type that no longer has items, reset to 'all'
  useEffect(() => {
    if (filterKey !== 'all' && !availableTypes.has(filterKey as ItemType)) {
      handleSetFilterKey('all');
    }
  }, [availableTypes, filterKey]);

  // Filter
  const filtered = useMemo(() => {
    if (filterKey === 'all') return baseItems;
    return baseItems.filter(item => item.type === filterKey);
  }, [baseItems, filterKey]);

  // Sort — build a map of id→wallpaper for createdAt lookups
  const libraryMap = useMemo(
    () => new Map((libraryItems ?? []).map(w => [w.id, w])),
    [libraryItems],
  );

  const sorted = useMemo(() => {
    const typeOrder: Record<ItemType, number> = { wallpaper: 0, 'live-wallpaper': 1, ringtone: 2, icon: 3 };
    const items = filtered.map(item => ({ item, wallpaper: libraryMap.get(item.id) }));
    if (sortKey === 'name') {
      items.sort((a, b) => a.item.title.localeCompare(b.item.title));
    } else if (sortKey === 'type') {
      items.sort((a, b) => (typeOrder[a.item.type] ?? 99) - (typeOrder[b.item.type] ?? 99));
    }
    // 'recent': preserve the order supplied by the parent, which is already
    // sorted by the user's purchase timestamp (purchasedAt) descending.
    return items.map(({ item }) => item);
  }, [filtered, sortKey, libraryMap]);

  const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: 'recent', label: 'Recent' },
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
  ];

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* ── Sort + Filter row — only show when there are items ─── */}
      {baseItems.length > 0 && <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '9px 14px 16px',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      } as React.CSSProperties}>
        <span style={{
          fontSize: 12,
          color: 'rgba(240,240,245,0.38)',
          fontFamily: "'Syne', sans-serif",
          fontWeight: 500,
          flexShrink: 0,
          marginRight: 2,
        }}>
          Sort by:
        </span>
        {SORT_OPTIONS.map(({ key, label }) => {
          const active = sortKey === key;
          return (
            <motion.button
              key={key}
              whileTap={{ scale: 0.92 }}
              onClick={() => handleSetSortKey(key)}
              style={{
                height: 30,
                padding: '0 14px',
                borderRadius: 9999,
                border: active ? 'none' : '1px solid rgba(255,255,255,0.13)',
                background: active ? '#46f5e0' : 'transparent',
                color: active ? '#0a0a10' : 'rgba(240,240,245,0.5)',
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                fontFamily: "'Syne', sans-serif",
                letterSpacing: active ? '-0.01em' : '0',
                flexShrink: 0,
                transition: 'background 0.18s ease, color 0.18s ease, border-color 0.18s ease',
                boxShadow: 'none',
              }}
            >
              {label}
            </motion.button>
          );
        })}

        {/* Divider and Filter dropdown — only show when there are multiple item types */}
        {availableTypes.size > 1 && (
          <div style={{
            width: 1,
            height: 18,
            background: 'rgba(255,255,255,0.18)',
            flexShrink: 0,
            margin: '0 4px',
          }} />
        )}

        {/* Filter dropdown — only show when there are multiple item types */}
        {availableTypes.size > 1 && <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
          <DropdownMenuTrigger asChild>
            <motion.button
              whileTap={{ scale: 0.94 }}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 9999,
                border: '1px solid rgba(70,245,224,0.35)',
                background: 'rgba(70,245,224,0.15)',
                color: '#46f5e0',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'Syne', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                transition: 'background 0.18s, color 0.18s, border-color 0.18s',
              }}
            >
              <Filter size={12} strokeWidth={2} />
              Filter
            </motion.button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            style={{
              background: 'rgba(20,18,28,0.98)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 14,
              minWidth: 220,
              padding: 6,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{
              padding: '8px 12px 6px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(240,240,245,0.42)',
              fontFamily: "'Syne', sans-serif",
            }}>
              Type
            </div>
            {(Object.entries(FILTER_LABELS) as [FilterKey, string][])
              .filter(([key]) => key === 'all' || availableTypes.has(key as ItemType))
              .map(([key, label]) => {
              const selected = filterKey === key;
              return (
                <DropdownMenuItem
                  key={key}
                  onSelect={(e) => { e.preventDefault(); handleSetFilterKey(key); setFilterOpen(false); }}
                  style={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: 13,
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#46f5e0' : 'rgba(240,240,245,0.78)',
                    cursor: 'pointer',
                    borderRadius: 8,
                    padding: '9px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span>{label}</span>
                  {selected && <Check size={14} strokeWidth={2.5} color="#46f5e0" />}
                </DropdownMenuItem>
              );
            })}

            <div style={{
              height: 1,
              background: 'rgba(255,255,255,0.08)',
              margin: '6px 4px',
            }} />

            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); handleSetFilterKey('all'); setFilterOpen(false); }}
              style={{
                fontFamily: "'Syne', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: filterKey !== 'all' ? '#f0f0f5' : 'rgba(240,240,245,0.4)',
                cursor: filterKey !== 'all' ? 'pointer' : 'default',
                borderRadius: 8,
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <RotateCcw size={13} strokeWidth={2.2} />
              Reset filter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
      </div>}

      {/* ── Card grid ────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {sorted.length === 0 ? (
          <LibraryEmptyState
            filterKey={filterKey}
            isFilterActive={filterKey !== 'all' && baseItems.length > 0}
            onBrowse={onBrowse}
          />
        ) : (
          <motion.div
            key={`grid-${filterKey}-${sortKey}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {sortKey !== 'recent' ? (
              // Non-recent sorts: render ringtones first (full-width), then visual grid
              <>
                {sorted.filter(item => item.type === 'ringtone').length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 7px', marginBottom: sorted.filter(item => item.type !== 'ringtone').length > 0 ? 20 : 0 }}>
                    {sorted.filter(item => item.type === 'ringtone').map((item, i) => (
                      item.wallpaper ? (
                        <LibraryRingtoneRow
                          key={item.id}
                          item={item}
                          index={i}
                          isFavorited={favoritedIds.has(item.wallpaper.id)}
                          onToggleFavorite={onToggleFavorite}
                          selectionMode={selectionMode}
                          isSelected={selection.has(item.id)}
                          onLongPressItem={onLongPressItem}
                          onToggleSelect={onToggleSelectItem}
                        />
                      ) : (
                        <LibraryRingtoneRow
                          key={item.id}
                          item={item}
                          index={i}
                          isFavorited={false}
                          onToggleFavorite={onToggleFavorite}
                          selectionMode={selectionMode}
                          isSelected={selection.has(item.id)}
                          onLongPressItem={onLongPressItem}
                          onToggleSelect={onToggleSelectItem}
                        />
                      )
                    ))}
                  </div>
                )}
                {sorted.filter(item => item.type !== 'ringtone').length > 0 && (() => {
                  const visualItems = sorted.filter(item => item.type !== 'ringtone');
                  // Chunk into rows of 3; each row's column count adapts to its own size
                  // so trailing rows with 1 or 2 items stretch to fill the available width.
                  const chunks: DemoItem[][] = [];
                  for (let c = 0; c < visualItems.length; c += 3) {
                    chunks.push(visualItems.slice(c, c + 3));
                  }
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {chunks.map((chunk, rowIdx) => (
                        <div
                          key={`row-${rowIdx}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${chunk.length}, 1fr)`,
                            gap: 8,
                            padding: '0 7px',
                          }}
                        >
                          {chunk.map((item, i) => {
                            const absoluteIndex = rowIdx * 3 + i;
                            return (
                              <div key={item.id} style={{ minWidth: 0, overflow: 'hidden' }}>
                                {item.wallpaper ? (
                                  <LibraryCard
                                    item={item}
                                    index={absoluteIndex}
                                    selectionMode={selectionMode}
                                    isSelected={selection.has(item.id)}
                                    onLongPressItem={onLongPressItem}
                                    onToggleSelect={onToggleSelectItem}
                                  />
                                ) : (
                                  <LibraryCard
                                    item={item}
                                    index={absoluteIndex}
                                    selectionMode={selectionMode}
                                    isSelected={selection.has(item.id)}
                                    onLongPressItem={onLongPressItem}
                                    onToggleSelect={onToggleSelectItem}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            ) : (
              // Recent sort: render ALL items in their sorted order as a single continuous list.
              // Ringtones are full-width rows; visual items are grouped into 3-column rows
              // while preserving the overall sort order interleaved with ringtones.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {(() => {
                  const rows: React.ReactNode[] = [];
                  let i = 0;
                  while (i < sorted.length) {
                    const item = sorted[i];
                    if (item.type === 'ringtone') {
                      // Full-width ringtone row
                      rows.push(
                        <div key={item.id} style={{ padding: '0 7px', marginBottom: 10 }}>
                          {item.wallpaper ? (
                            <LibraryRingtoneRow
                              item={item}
                              index={i}
                              isFavorited={favoritedIds.has(item.wallpaper.id)}
                              onToggleFavorite={onToggleFavorite}
                              selectionMode={selectionMode}
                              isSelected={selection.has(item.id)}
                              onLongPressItem={onLongPressItem}
                              onToggleSelect={onToggleSelectItem}
                            />
                          ) : (
                            <LibraryRingtoneRow
                              item={item}
                              index={i}
                              isFavorited={false}
                              onToggleFavorite={onToggleFavorite}
                              selectionMode={selectionMode}
                              isSelected={selection.has(item.id)}
                              onLongPressItem={onLongPressItem}
                              onToggleSelect={onToggleSelectItem}
                            />
                          )}
                        </div>
                      );
                      i++;
                    } else {
                      // Collect consecutive non-ringtone items for a grid row
                      const groupStart = i;
                      const group: DemoItem[] = [];
                      while (i < sorted.length && sorted[i].type !== 'ringtone') {
                        group.push(sorted[i]);
                        i++;
                      }
                      // Chunk group into rows of 3 so trailing rows with 1 or 2 items
                      // stretch to fill the available width instead of being clipped.
                      const groupChunks: DemoItem[][] = [];
                      for (let c = 0; c < group.length; c += 3) {
                        groupChunks.push(group.slice(c, c + 3));
                      }
                      groupChunks.forEach((chunk, chunkIdx) => {
                        const isLastChunk = chunkIdx === groupChunks.length - 1;
                        const chunkStart = groupStart + chunkIdx * 3;
                        rows.push(
                          <div
                            key={`visual-group-${groupStart}-row-${chunkIdx}`}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: `repeat(${chunk.length}, 1fr)`,
                              gap: 8,
                              padding: '0 7px',
                              marginBottom: (isLastChunk && i >= sorted.length) ? 0 : 10,
                            }}
                          >
                            {chunk.map((gItem, gi) => (
                              <div key={gItem.id} style={{ minWidth: 0, overflow: 'hidden' }}>
                                {gItem.wallpaper ? (
                                  <LibraryCard
                                    item={gItem}
                                    index={chunkStart + gi}
                                    selectionMode={selectionMode}
                                    isSelected={selection.has(gItem.id)}
                                    onLongPressItem={onLongPressItem}
                                    onToggleSelect={onToggleSelectItem}
                                  />
                                ) : (
                                  <LibraryCard
                                    item={gItem}
                                    index={chunkStart + gi}
                                    selectionMode={selectionMode}
                                    isSelected={selection.has(gItem.id)}
                                    onLongPressItem={onLongPressItem}
                                    onToggleSelect={onToggleSelectItem}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      });
                    }
                  }
                  return rows;
                })()}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LibraryView;
